import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Warning, ClockCountdown, CaretDown, CaretUp, Package, User, CalendarBlank,
  MagnifyingGlass, CaretLeft, CaretRight, ArrowLeft,
} from '@phosphor-icons/react';
import { ModuleShell, SkeletonBlock } from '../../../components/module-ui';
import { apiPath } from '../../../config/apiBase';
import Product360Page from './Product360Page';
import Assignee360Page from './Assignee360Page';
import Performance360Page from './Performance360Page';
import Breakdown360Page from './Breakdown360Page';
import Volume360Page from './Volume360Page';
import Solved360Page from './Solved360Page';

const TICKETS_API = apiPath('/api/tickets');
const PAGE_SIZE = 10;

const STATE_COLORS = {
  breached: { bg: '#FEE2E2', fg: '#B91C1C', border: '#FECACA' },
  at_risk: { bg: '#FEF3C7', fg: '#B45309', border: '#FDE68A' },
  assignment_breached: { bg: '#FCE7F3', fg: '#9D174D', border: '#FBCFE8' },
  assignment_at_risk: { bg: '#E0E7FF', fg: '#3730A3', border: '#C7D2FE' },
};

function authConfig() {
  const token = localStorage.getItem('token');
  return { withCredentials: true, headers: { Authorization: `Bearer ${token}` } };
}

function StateBadge({ state }) {
  const c = STATE_COLORS[state] || { bg: '#F3F4F6', fg: '#374151', border: '#E5E7EB' };
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 600,
        background: c.bg,
        color: c.fg,
        border: `1px solid ${c.border}`,
        textTransform: 'capitalize',
        whiteSpace: 'nowrap',
      }}
    >
      {(state || '').replace(/_/g, ' ')}
    </span>
  );
}

function PipelineTrack({ pins = [] }) {
  return (
    <ol className="sla-pipeline-track" aria-label="Ticket pipeline">
      {pins.map((pin, idx) => (
        <li
          key={`${pin.pin}-${idx}`}
          className={`sla-pipeline-pin${pin.critical ? ' is-critical' : ''}${pin.done ? ' is-done' : ''}`}
        >
          <div className="sla-pipeline-pin-dot" aria-hidden />
          <div className="sla-pipeline-pin-body">
            <div className="sla-pipeline-pin-label">{pin.label}</div>
            <div className="sla-pipeline-pin-what"><strong>What:</strong> {pin.what || '—'}</div>
            <div className="sla-pipeline-pin-who"><strong>Who:</strong> {pin.who || '—'}</div>
            <div className="sla-pipeline-pin-when"><strong>When:</strong> {pin.when_ist || '—'}</div>
          </div>
        </li>
      ))}
    </ol>
  );
}

/**
 * Dashboard drill-downs:
 * Product / Assignee / Performance / Domain / Client / Volume 360° views.
 * Domain+client without mode still open Breakdown 360 (not the old thin list).
 */
export default function TicketPipelinePage() {
  const [searchParams] = useSearchParams();
  const productId = searchParams.get('product_id') || '';
  const assignedTo = searchParams.get('assigned_to') || '';
  const mode = searchParams.get('mode') || '';
  const domain = searchParams.get('domain') || '';
  const client = searchParams.get('client') || '';

  if (mode === 'solved') {
    return <Solved360Page />;
  }
  if (productId && productId !== '__none__') {
    return <Product360Page productId={productId} />;
  }
  if (assignedTo) {
    return <Assignee360Page userId={assignedTo} />;
  }
  if (mode === 'performance') {
    return <Performance360Page />;
  }
  if (mode === 'volume' || searchParams.has('created_on')) {
    return <Volume360Page />;
  }
  if (mode === 'domain' || domain) {
    return <Breakdown360Page mode="domain" />;
  }
  if (mode === 'client' || client) {
    return <Breakdown360Page mode="client" />;
  }
  return <TicketPipelineListPage />;
}

function TicketPipelineListPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const domain = searchParams.get('domain') || '';
  const productId = searchParams.get('product_id') || '';
  const client = searchParams.get('client') || '';
  const assignedTo = searchParams.get('assigned_to') || '';

  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState({});
  const [error, setError] = useState(null);

  const fetchPipeline = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page,
        page_size: PAGE_SIZE,
        status_group: 'open',
      };
      if (domain) params.domain = domain;
      if (productId) params.product_id = productId;
      if (client) params.client = client;
      if (assignedTo) params.assigned_to = assignedTo;
      if (q.trim()) params.q = q.trim();
      const res = await axios.get(`${TICKETS_API}/dashboard/ticket-pipeline`, {
        ...authConfig(),
        params,
      });
      setPayload(res.data);
    } catch (e) {
      console.error('Ticket pipeline fetch failed', e);
      setError('Could not load ticket pipeline');
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [domain, productId, client, assignedTo, page, q]);

  useEffect(() => {
    fetchPipeline();
  }, [fetchPipeline]);

  useEffect(() => {
    setPage(1);
    setExpanded({});
  }, [domain, productId, client, assignedTo]);

  useEffect(() => {
    const t = setTimeout(() => {
      const next = searchInput.trim();
      if (next === q) return;
      setPage(1);
      setQ(next);
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput, q]);

  const items = payload?.items || [];
  const summary = payload?.summary || {};
  const pagination = payload?.pagination || {
    page: 1, page_size: PAGE_SIZE, total: 0, total_pages: 1, has_prev: false, has_next: false,
  };
  const from = pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.page_size + 1;
  const to = Math.min(pagination.page * pagination.page_size, pagination.total);

  const listLink = () => {
    const sp = new URLSearchParams();
    sp.set('status_group', 'open');
    if (domain) sp.set('domain', domain);
    if (productId) sp.set('product_id', productId);
    if (client) sp.set('client', client);
    if (assignedTo) sp.set('assigned_to', assignedTo);
    return `/dashboard/tickets/list?${sp.toString()}`;
  };

  if (loading && !payload) {
    return (
      <ModuleShell title="Ticket pipeline" subtitle="Loading…">
        <SkeletonBlock rows={8} />
      </ModuleShell>
    );
  }

  return (
    <ModuleShell
      title="Ticket pipeline"
      subtitle={payload?.title || 'Drill-down by domain, app, and client'}
      headerActions={(
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" className="tk-btn tk-btn-secondary" onClick={() => navigate('/dashboard')}>
            <ArrowLeft size={16} />
            Overview
          </button>
          <Link to={listLink()} className="tk-btn tk-btn-secondary">
            Open ticket list
          </Link>
        </div>
      )}
    >
      <div className="sla-pipeline-panel" data-testid="ticket-pipeline-page">
        <div className="sla-pipeline-summary" role="group" aria-label="Pipeline summary">
          <div className="sla-summary-chip">
            <span className="sla-summary-chip-value">{summary.matched || 0}</span>
            <span className="sla-summary-chip-label">Open tickets</span>
          </div>
          <div className="sla-summary-chip">
            <span className="sla-summary-chip-value">{summary.breached || 0}</span>
            <span className="sla-summary-chip-label">SLA breached</span>
          </div>
          <div className="sla-summary-chip">
            <span className="sla-summary-chip-value">{summary.at_risk || 0}</span>
            <span className="sla-summary-chip-label">SLA at risk</span>
          </div>
        </div>

        <div className="sla-pipeline-filters">
          <div style={{ fontSize: 12, color: '#6B7280' }}>
            {domain && <span style={{ marginRight: 10 }}><strong>Domain:</strong> {domain}</span>}
            {productId && <span style={{ marginRight: 10 }}><strong>App:</strong> {productId === '__none__' ? 'None' : (payload?.title || productId)}</span>}
            {client && <span><strong>Client:</strong> {client}</span>}
            {!domain && !productId && !client && <span>All open tickets</span>}
          </div>
          <form
            className="sla-api-search"
            onSubmit={(e) => {
              e.preventDefault();
              const next = searchInput.trim();
              setPage(1);
              setQ(next);
            }}
            role="search"
          >
            <MagnifyingGlass size={16} className="sla-api-search-icon" aria-hidden />
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search ticket, person, product…"
              data-testid="ticket-pipeline-search"
            />
            <button type="submit" className="tk-btn tk-btn-secondary">Search</button>
          </form>
        </div>

        {error && <p style={{ color: '#B91C1C', fontSize: 13 }}>{error}</p>}

        {items.length === 0 ? (
          <div className="sla-pipeline-empty">
            <ClockCountdown size={28} color="#9CA3AF" />
            <p>{q ? `No tickets match “${q}”.` : 'No open tickets in this drill-down.'}</p>
          </div>
        ) : (
          <>
            <div className="sla-pipeline-grid">
              {items.map((item) => {
                const open = !!expanded[item.ticket_no];
                const productName = item.product?.product_name || item.product?.product_key || '—';
                const assignees = (item.who?.assignee_names || []).join(', ') || 'Unassigned';
                return (
                  <div
                    key={item.ticket_no}
                    className={`sla-pipeline-card${open ? ' is-expanded' : ''}`}
                    data-testid="ticket-pipeline-row"
                  >
                    <div className="sla-pipeline-row-main">
                      <div className="sla-pipeline-row-top">
                        <Link
                          to={`/dashboard/tickets/list/${encodeURIComponent(item.ticket_no)}`}
                          className="sla-ticket-link"
                        >
                          {item.ticket_no}
                        </Link>
                        <StateBadge state={item.primary_state} />
                        {item.what?.priority && (
                          <span className={`badge badge-${item.what.priority}`}>{item.what.priority}</span>
                        )}
                      </div>
                      <div className="sla-pipeline-what" title={item.what?.subject}>
                        <strong>What:</strong> {item.what?.subject || '—'}
                      </div>
                      <div className="sla-pipeline-meta">
                        <span><User size={14} /> <strong>Who:</strong> {assignees}</span>
                        <span><CalendarBlank size={14} /> <strong>When:</strong> {item.when?.due_date_ist || item.when?.created_at_ist || '—'}</span>
                        <span><Package size={14} /> <strong>App:</strong> {productName}</span>
                        <span><Warning size={14} /> <strong>Client:</strong> {item.what?.client || '—'}</span>
                      </div>
                    </div>
                    <div className="sla-pipeline-row-actions">
                      <button
                        type="button"
                        className="tk-btn tk-btn-secondary"
                        onClick={() => setExpanded((prev) => ({ ...prev, [item.ticket_no]: !prev[item.ticket_no] }))}
                        aria-expanded={open}
                      >
                        {open ? <CaretUp size={16} /> : <CaretDown size={16} />}
                        Pipeline
                      </button>
                    </div>
                    {open && (
                      <div className="sla-pipeline-detail">
                        <div className="sla-pipeline-detail-grid">
                          <div>
                            <h4>Reporter</h4>
                            <p>{item.who?.reporter_name || '—'}{item.who?.reporter_email ? ` · ${item.who.reporter_email}` : ''}</p>
                          </div>
                          <div>
                            <h4>Domain</h4>
                            <p>{item.what?.domain || '—'} · {item.what?.status || '—'}</p>
                          </div>
                          <div>
                            <h4>Created</h4>
                            <p>{item.when?.created_at_ist || '—'} · {item.who?.created_by || '—'}</p>
                          </div>
                          <div>
                            <h4>SLA</h4>
                            <p>
                              {item.sla_policy?.name || '—'}
                              {item.when?.due_date_ist ? ` · due ${item.when.due_date_ist}` : ''}
                            </p>
                          </div>
                        </div>
                        <PipelineTrack pins={item.pipeline || []} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="sla-pagination" data-testid="ticket-pipeline-pagination">
              <span className="sla-pagination-meta">
                Showing {from}–{to} of {pagination.total}
                {q ? ` for “${q}”` : ''}
              </span>
              <div className="sla-pagination-buttons">
                <button
                  type="button"
                  className="tk-btn tk-btn-secondary"
                  disabled={!pagination.has_prev || loading}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  <CaretLeft size={16} />
                  Previous
                </button>
                <span className="sla-pagination-page">
                  Page {pagination.page} / {pagination.total_pages}
                </span>
                <button
                  type="button"
                  className="tk-btn tk-btn-secondary"
                  disabled={!pagination.has_next || loading}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                  <CaretRight size={16} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </ModuleShell>
  );
}
